import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Grid, Link, Text } from 'components/ui';
import InfoButton from 'components/InfoButton';
import basketUtils from 'utils/Basket';
import localeUtils from 'utils/LanguageLocale';
import isFunction from 'utils/functions/isFunction';
import checkoutUtils from 'utils/Checkout';
import frictionlessCheckoutUtils from 'utils/FrictionlessCheckout';

function renderGstHst(priceInfo) {
    const totalTax = Number(basketUtils.removeCurrency(priceInfo?.tax));

    if (totalTax === 0) {
        return priceInfo?.tax;
    }

    const goodsAndServicesTax = Number(basketUtils.removeCurrency(priceInfo?.goodsAndServicesTax));
    const harmonizedSalesTax = Number(basketUtils.removeCurrency(priceInfo?.harmonizedSalesTax));
    const canadaSalesTax = Number(goodsAndServicesTax + harmonizedSalesTax);
    const canadaSalesTaxFormatted = localeUtils.getFormattedPrice(canadaSalesTax, false, true);

    return canadaSalesTaxFormatted;
}

// A helper to compute tax value
function computeTaxValue(priceInfo, showZeroDollarsTax) {
    const tbdValue = basketUtils.getCurrency(priceInfo.orderTotal) + '-.--';
    const isShipAddressComplete = checkoutUtils.isShipAddressComplete();

    let taxValue =
        (!priceInfo.giftCardSubtotal && priceInfo.merchandiseSubtotal !== priceInfo.orderTotal) ||
        priceInfo.giftCardShipping ||
        (isShipAddressComplete && priceInfo.tax)
            ? priceInfo.tax || priceInfo.stateTax || tbdValue
            : tbdValue;

    if (showZeroDollarsTax) {
        taxValue = localeUtils.isFrench() ? '0,00 $' : '$0.00';
    }

    return taxValue;
}

// Release 2 (Default): Use taxText with isBopis
// Release 2 still uses gstHstText for Canada
// Keep this old school since most features are for Release 3 now in FC
function ReleaseTwoTaxSection({
    locales: { gstHstText, taxText, otherFees, moreInfo }, infoModalCallback, priceInfo, isBopis, taxValue
}) {
    const taxDisplayValue = localeUtils.isCanada() ? renderGstHst(priceInfo) : taxValue;
    const taxLabelText = localeUtils.isCanada() ? gstHstText : `${taxText}${isBopis ? ' ' + otherFees : ''}`;

    return (
        <Grid
            gap={2}
            columns='1fr auto'
            role='row'
            aria-label={localeUtils.isCanada() ? `${taxLabelText}: ${taxDisplayValue}` : undefined}
            tabIndex={localeUtils.isCanada() ? 0 : undefined}
        >
            <span role='cell'>
                {localeUtils.isCanada() ? (
                    gstHstText
                ) : (
                    <Link
                        padding={1}
                        margin={-1}
                        data-at={Sephora.debug.dataAt('tax_btn')}
                        onClick={isFunction(infoModalCallback) ? infoModalCallback : undefined}
                        aria-label={`${taxLabelText}: ${taxDisplayValue}. ${moreInfo}`}
                    >
                        {taxText} {isBopis && otherFees}
                        {isFunction(infoModalCallback) && (
                            <InfoButton
                                aria-label={moreInfo}
                                isOutline
                                marginLeft={-1}
                            />
                        )}
                    </Link>
                )}
            </span>
            <Text
                data-at={Sephora.debug.dataAt('bsk_total_tax')}
                role='cell'
            >
                {taxDisplayValue}
            </Text>
        </Grid>
    );
}

function Tax({
    locales: {
        gstHstText, taxText, otherFees, moreInfo, taxesAndDutiesText
    },
    showZeroDollarsTax,
    infoModalCallback,
    priceInfo,
    isBopis
}) {
    const isRelease3Enabled = frictionlessCheckoutUtils.isFCReleaseThreeEnabled();

    // Replace previous inline tax computation with helper call
    const taxValue = computeTaxValue(priceInfo, showZeroDollarsTax);

    // Release 3 (PR #6259 + #6294 + #6434): Conditional tax label based on locale and conditions
    if (isRelease3Enabled) {
        // PR #6434: Determine tax label based on locale and conditions
        const isCanada = localeUtils.isCanada();
        const hasGstOrHst = priceInfo?.goodsAndServicesTax || priceInfo?.harmonizedSalesTax;

        let taxLabel;
        let showInfoButton = true;

        if (isCanada && hasGstOrHst) {
            // CA locale with GST/HST: show "Estimated GST/HST" without info button
            taxLabel = gstHstText;
            showInfoButton = !!priceInfo?.harmonizedSalesTax;
        } else if (!isCanada && isBopis) {
            // US locale BOPIS: show "Estimated Tax & Other Fees"
            taxLabel = taxText + ' ' + otherFees;
        } else if (!isCanada) {
            // US locale SAD: show "Estimated Tax"
            taxLabel = taxText;
        } else {
            // CA locale without GST/HST (stateTax): show "Estimated Taxes And Duties" without info button
            taxLabel = taxesAndDutiesText;
            showInfoButton = false;
        }

        // Determine tax value to display
        const displayTaxValue = isCanada && hasGstOrHst ? renderGstHst(priceInfo) : taxValue;

        return (
            <Grid
                gap={2}
                columns='1fr auto'
                role='row'
                aria-label={!showInfoButton ? `${taxLabel}: ${displayTaxValue}` : undefined}
                tabIndex={!showInfoButton ? 0 : undefined}
            >
                <span role='cell'>
                    {showInfoButton ? (
                        <Link
                            padding={1}
                            margin={-1}
                            data-at={Sephora.debug.dataAt('tax_btn')}
                            onClick={isFunction(infoModalCallback) ? infoModalCallback : undefined}
                            aria-label={`${taxLabel}: ${displayTaxValue}. ${moreInfo}`}
                        >
                            {taxLabel}
                            {isFunction(infoModalCallback) && (
                                <InfoButton
                                    aria-label={moreInfo}
                                    isOutline
                                    marginLeft={-1}
                                />
                            )}
                        </Link>
                    ) : (
                        taxLabel
                    )}
                </span>
                <Text
                    data-at={Sephora.debug.dataAt('bsk_total_tax')}
                    role='cell'
                >
                    {displayTaxValue}
                </Text>
            </Grid>
        );
    }

    // Release 2 (Default): Use taxText with isBopis
    return (
        <ReleaseTwoTaxSection
            locales={{ gstHstText, taxText, otherFees, moreInfo }}
            infoModalCallback={infoModalCallback}
            priceInfo={priceInfo}
            isBopis={isBopis}
            taxValue={taxValue}
        />
    );
}

export default wrapFunctionalComponent(Tax, 'Tax');
